import type { RefObject } from 'react'
import { useLayoutEffect } from 'react'

/**
 * Keeps a :root CSS variable in sync with an element’s height.
 *
 * @param ref      React ref pointing at the element to observe
 * @param varName  The CSS variable to write (default: --elem-h)
 */
export function useHeightObserver(
  ref: RefObject<HTMLElement | null>,
  varName: string = '--elem-h',
) {
  useLayoutEffect(() => {
    if (!ref) return
    const element = ref.current
    if (!element) return

    const setVar = (h: number) =>
      document.documentElement.style.setProperty(varName, `${h - 1}px`)

    setVar(element.offsetHeight)

    const resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry) throw new Error('No entry')
      setVar(entry.contentRect.height)
    })
    resizeObserver.observe(element)

    return () => resizeObserver.disconnect()
  }, [ref, varName])
}
